<template>
    <b-container class="p-4" fluid>
        <h4>Tender offer modeling</h4>
        <b-row class="pb-4">
            <b-col>
                <b-card-group deck>
                    <b-card title="Tender offer parameters" title-tag="h5">
                        <b-form-group
                            description="The price at which each share will be purchased"
                            label="Tender offer price:"
                            label-cols="4"
                            label-align="right"
                            label-for="price"
                        >
                            <b-form-input
                                id="price"
                                v-model.lazy="params.price"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '$',
                                    suffix: '/share',
                                }"
                            />
                        </b-form-group>
                        <b-form-group
                            description="The fair market value at the time of the tender offer"
                            label="Fair market value:"
                            label-cols="4"
                            label-align="right"
                            label-for="fmv"
                        >
                            <b-form-input
                                id="fmv"
                                v-model.lazy="params.fmv"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '$',
                                    suffix: '/share',
                                }"
                            />
                        </b-form-group>
                        <b-form-group
                            description="Participants can sell up to this percent of vested"
                            label="Max vested percent:"
                            label-cols="4"
                            label-align="right"
                            label-for="max-vested"
                        >
                            <b-form-input
                                id="max-vested"
                                v-model.lazy="params.max_vested"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '',
                                    suffix: '%',
                                    precision: 0,
                                }"
                            />
                        </b-form-group>
                    </b-card>
                    <b-card title="Shares" title-tag="h5">
                        <b-form-group
                            description="The number of vested shares eligible to sell"
                            label="Vested:"
                            label-cols="4"
                            label-align="right"
                            label-for="vested"
                        >
                            <b-form-input
                                id="vested"
                                v-model.lazy="shares.vested"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '',
                                    suffix: ' shares',
                                    precision: 0,
                                }"
                            />
                        </b-form-group>
                        <b-form-group
                            description="The fair market value per share at the time of exercise/purchase"
                            label="Cost basis:"
                            label-cols="4"
                            label-align="right"
                            label-for="cost-basis"
                        >
                            <b-form-input
                                id="cost-basis"
                                v-model.lazy="shares.cost_basis"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '$',
                                    suffix: '/share',
                                }"
                            />
                        </b-form-group>
                        <b-form-group
                            description="Qualified shares must have been held for at least 1 year (and 2 years after option grant)"
                            label="Disposition:"
                            label-cols="4"
                            label-align="right"
                            label-for="disposition"
                        >
                            <b-form-select
                                id="disposition"
                                v-model="shares.disposition"
                                :options="[
                                    { value: 'qualified', text: 'Qualified' },
                                    { value: 'disqualified', text: 'Disqualified' },
                                ]"
                            />
                        </b-form-group>

                    </b-card>
                    <b-card title="Tax" title-tag="h5">
                        <b-form-group
                            description="Your ordinary income tax rate"
                            label="Tax rate:"
                            label-cols="4"
                            label-align="right"
                            label-for="tax-rate"
                        >
                            <b-form-input
                                id="tax-rate"
                                v-model.lazy="params.tax_rate"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '',
                                    suffix: '%',
                                    precision: 0,
                                }"
                            />
                        </b-form-group>
                        <b-form-group
                            description="Your capital gains tax rate"
                            label="Capital gains tax rate:"
                            label-cols="4"
                            label-align="right"
                            label-for="cap-gains-tax-rate"
                        >
                            <b-form-input
                                id="cap-gains-tax-rate"
                                v-model.lazy="params.cap_gains_tax_rate"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '',
                                    suffix: '%',
                                    precision: 0,
                                }"
                            />
                        </b-form-group>
                        <b-form-group
                            description="The future price per share (to calculate losses)"
                            label="Future price per share:"
                            label-cols="4"
                            label-align="right"
                            label-for="future-fmv"
                        >
                            <b-form-input
                                id="future-fmv"
                                v-model.lazy="shares.future_fmv"
                                v-money="{
                                    decimal: '.',
                                    thousands: ',',
                                    prefix: '$',
                                    suffix: '/share',
                                }"
                            />
                        </b-form-group>
                    </b-card>
                </b-card-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-table
                    outlined
                    small
                    striped
                    responsive="sm"
                    :fields="fields"
                    :items="rows"
                >
                    <template slot="HEAD_actions">
                        <b-btn
                            v-b-modal.input-modal
                            v-b-tooltip.hover
                            title="Add a scenario"
                            size="sm"
                            variant="outline-primary"
                            pill
                        >
                            +
                        </b-btn>
                    </template>
                    <template v-for="(percent, idx) in scenarios" :slot="`HEAD_Scenario ${idx + 1}`">
                        Scenario {{ idx + 1 }}
                        <b-btn
                            v-b-tooltip.hover
                            title="Remove scenario"
                            size="sm"
                            variant="outline-danger"
                            pill
                            :key="percent"
                            @click="removeCol(percent)"
                        >
                            -
                        </b-btn>
                    </template>
                </b-table>
                <b-modal
                    ref="modal"
                    id="input-modal"
                    size="md"
                    title="Add a scenario"
                    @ok="addColumn"
                    @cancel="add_vested_percent = null"
                    @shown="() => $refs.modalInput.focus()"
                >
                    <b-form-group
                        description="Sell this percent of vested"
                        label="Percent:"
                        label-cols-sm="3"
                        label-align-sm="right"
                        label-for="add-vested-percent"
                    >
                        <b-form-input
                            ref="modalInput"
                            id="add-vested-percent"
                            @keyup.enter="() => { $refs.modal.hide(); addColumn(); }"
                            v-model.lazy="add_vested_percent"
                            v-money="{
                                decimal: '.',
                                thousands: ',',
                                prefix: '',
                                suffix: '%',
                                precision: 1,
                            }"
                        />
                    </b-form-group>
                </b-modal>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
    // localStorage persistence
    const STORAGE_KEY = 'tender-offer-modeling';
    const defaultParams = {
        price: '',
        fmv: '',
        tax_rate: '24%',
        cap_gains_tax_rate: '15%',
        max_vested: '100%',
    };
    const defaultShares = {
        vested: '0',
        disposition: 'qualified',
        cost_basis: '',
        future_fmv: '',
    };
    const storage = {
        fetch(key) {
            const items = JSON.parse(localStorage.getItem(`${STORAGE_KEY}-${key}`) || '{}');
            switch (key) {
                case 'params':
                    return { ...defaultParams, ...items };
                case 'shares':
                    return { ...defaultShares, ...items };
                case 'scenarios':
                    return Array.isArray(items) ? items : [];
                default:
                    return items;
            }
        },
        save(key, items) {
            localStorage.setItem(`${STORAGE_KEY}-${key}`, JSON.stringify(items));
        }
    };
    export default {
        name: 'app',
        data() {
            return {
                params: storage.fetch('params'),
                shares: storage.fetch('shares'),
                scenarios: storage.fetch('scenarios'),
                add_vested_percent: null,
            };
        },
        watch: {
            params: {
                deep: true,
                handler(params) {
                    storage.save('params', params);
                },
            },
            shares: {
                deep: true,
                handler(shares) {
                    storage.save('shares', shares);
                },
            },
            scenarios: {
                deep: true,
                handler(scenarios) {
                    storage.save('scenarios', scenarios.filter(x => x));
                },
            },
        },
        computed: {
            fields() {
                const formatter = (value, key, item) => {
                    switch (item.key) {
                        case 'Percent vested':
                            return value || 0;
                        case 'Can sell':
                        case 'Remaining shares':
                            return String(value || 0).replace(/\B(?=(?:\d{3})+(?!\d))/g,',');
                        default:
                            return `$${String((value || 0).toFixed(2)).replace(/\B(?=(?:\d{3})+(?!\d))/g,',')}`;
                    }
                };
                const fields = [
                    {
                        key: 'key',
                        label: '',
                    },
                    {
                        key: 'max',
                        formatter,
                    },
                ];
                this.scenarios.forEach((_, idx) => {
                    fields.push({
                        key: `Scenario ${idx + 1}`,
                        formatter,
                    });
                });
                fields.push('actions');
                return fields;
            },
            rows() {
                const vals = this.calcValues(this.params.max_vested);
                const rows = [
                    {
                        key: 'Percent vested',
                        max: vals[0],
                    },
                    {
                        key: 'Can sell',
                        max: vals[1],
                    },
                    {
                        key: 'Pre-tax proceeds',
                        max: vals[2],
                    },
                    {
                        key: 'Estimated tax withholdings',
                        max: vals[3],
                    },
                    {
                        key: '(Capital gains only)',
                        max: vals[4],
                    },
                    {
                        key: '(Ordinary income only)',
                        max: vals[5],
                    },
                    {
                        key: 'Post-tax proceeds',
                        max: vals[6],
                        _cellVariants: { max: 'success' },
                    },
                    {
                        key: 'Remaining shares',
                        max: vals[7],
                    },
                    {
                        key: 'Future remaining shares value',
                        max: vals[8],
                    },
                    {
                        'key': 'Lost value at future exit',
                        'max': vals[9],
                    },
                ];
                this.scenarios.forEach((percent, idx) => {
                    if (!percent) {
                        return;
                    }
                    const vals = this.calcValues(percent);
                    rows.forEach((_, i) => {
                        rows[i][`Scenario ${idx + 1}`] = vals[i];
                        if (i === 6) {
                            rows[i]['_cellVariants'][`Scenario ${idx + 1}`] = 'success';
                        }
                    });
                });
                return rows;
            },
        },
        methods: {
            calcValues(percentStr) {
                const percent = Number.parseFloat(percentStr.replace(/[^0-9.]/g, '')) / 100;
                const capGainsRate = Number.parseInt(this.params.cap_gains_tax_rate.replace(/[^0-9]/g, '')) / 100;
                const oiRate = Number.parseInt(this.params.tax_rate.replace(/[^0-9]/g, '')) / 100;
                const vested = Number.parseInt(this.shares.vested.replace(/[^0-9]/g, ''));
                const price = Number.parseFloat(this.params.price.replace(/[^0-9.]/g, ''));
                const fmv = Number.parseFloat(this.params.fmv.replace(/[^0-9.]/g, ''));
                const basis = Number.parseFloat(this.shares.cost_basis.replace(/[^0-9.]/g, ''));
                const targetFmv = Number.parseFloat(this.shares.future_fmv.replace(/[^0-9.]/g, ''));
                const canSell = Math.floor(percent * vested);
                const held = vested - canSell;
                const heldValue = held * targetFmv;
                const futureValue = vested * targetFmv;
                const preTax = canSell * price;
                const lostValue = futureValue - heldValue - preTax;
                let oiTax = 0;
                let cgTax = 0;
                if (this.shares.disposition === 'disqualified') {
                    oiTax = (price - basis) * canSell * oiRate;
                } else if (price > fmv) {
                    cgTax = (fmv - basis) * canSell * capGainsRate;
                    oiTax = (price - fmv) * canSell * oiRate;
                } else {
                    cgTax = (price - basis) * canSell * capGainsRate;
                }
                return [
                    percentStr,
                    canSell,
                    preTax,
                    oiTax + cgTax,
                    cgTax,
                    oiTax,
                    preTax - oiTax - cgTax,
                    held,
                    heldValue,
                    lostValue,
                ]
            },
            addColumn() {
                const percentStr = this.add_vested_percent;
                const maxVested = Number.parseInt(this.params.max_vested.replace(/[^0-9]/g, '')) / 100;
                const percent = Number.parseFloat(percentStr.replace(/[^0-9.]/g, '')) / 100;
                const scenarios = [ ...this.scenarios ];
                this.add_vested_percent = null;
                if (percent <= 0) {
                    return;
                }
                if (percent > maxVested) {
                    return;
                }
                if (scenarios.indexOf(percentStr) >= 0) {
                    return;
                }
                scenarios.push(percentStr);
                scenarios.sort((a, b) => {
                    const x = Number.parseFloat(a.replace(/[^0-9.]/g, ''));
                    const y = Number.parseFloat(b.replace(/[^0-9.]/g, ''));
                    if (x < y) {
                        return 1;
                    } else if (x > y) {
                        return -1;
                    }
                    return 0;
                });
                this.scenarios = scenarios.filter(x => x);
            },
            removeCol(percentStr) {
                const scenarios = [ ...this.scenarios ];
                scenarios[scenarios.indexOf(percentStr)] = null;
                this.scenarios = scenarios.filter(x => x);
            },
        },
    };
</script>
